/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/navigation/EmployeeBottomNav.tsx
 * PURPOSE: Web-sync checklist A.2 — wires the existing-but-previously-unused
 *          BottomTabNavigation component into a single tab config shared by
 *          all 4 employee self-service routes (Dashboard / Attendance /
 *          Schedule / Leave), replacing EmployeeDashboard's hand-drawn 4-tab
 *          `<nav>` (which had no floating center button) and giving the
 *          other 3 routes a bottom nav for the first time.
 *          2026-08-15: web-sync checklist A.8 — swapped the emoji-as-icon
 *          placeholders for real `lucide-react` icons (already a project
 *          dependency), matching the shape of mobile's Ionicons per tab.
 *          2026-08-26: bottom-nav web đủ 5 mục GIỐNG HỆT mobile/thiết kế nguồn
 *          (Trang chủ · Lịch · Chấm công giữa nổi · Tiện ích · Tôi) — tab thứ 5
 *          "Tiện ích" → /utilities (launcher page + RequireRole route đã mount
 *          sẵn trong App.tsx từ web-sync A.3).
 *          Căn cứ (đã commit, tra được từ remote): WIREFRAME-V0-MASTER-REGISTRY.md
 *          §2 `SC-019-EMP-HOME` — wireframe CHỐT 2026-08-26 (@ba), mục NAVIGATION
 *          liệt kê đúng 5 mục dùng chung web+mobile; thiết kế nguồn `emp-01`
 *          (design-imports/.../employee-app-v2/index.html L147-153) + mobile
 *          as-built (BottomTabNavigator.tsx TAB_CONFIG) đều 5 mục.
 *          (Ghi chú cũ 2026-08-17 về ranh giới B1 4-tab bị SUPERSEDED — xem
 *          BS-301 §0.3 B1, đã amend cùng ngày.)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-25
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero-Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (Component Library)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * @business-context
 * - Domain: UI Components, Vietnamese F&B Employee Self-Service Navigation
 * - Impact: User Experience — consistent navigation across employee routes
 * - Stakeholders: Employees (71)
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Navigation
 * - Pattern: Shared config-driven component (BottomTabNavigation)
 * - Dependencies: React 18, React Router, lucide-react, components/navigation/BottomTabNavigation
 *
 * @cross-references
 * - Wireframe SSOT (5-tab): docs/02-design/04-ui-ux-design/persona-flows/
 *   WIREFRAME-V0-MASTER-REGISTRY.md §2 `SC-019-EMP-HOME` (CHỐT 2026-08-26, @ba)
 * - Ranh giới cũ đã amend: docs/01-planning/02-uat-len-lich-cham-cong/
 *   BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN.md §0.3 B1
 * - Thiết kế nguồn: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/
 *   v0/employee-app-v2/index.html (`emp-01` L147-153)
 * - Component: frontend/src/components/navigation/BottomTabNavigation.tsx
 * - Mobile source of truth (5-tab):
 *   mobile/src/navigation/BottomTabNavigator.tsx (TAB_CONFIG)
 * - Used by: EmployeeDashboard.tsx, AttendanceModern.tsx, ScheduleModern.tsx,
 *   LeaveRequestModern.tsx, Utilities.tsx
 *  */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { Home, Calendar, Fingerprint, LayoutGrid, User } from 'lucide-react';
import { BottomTabNavigation } from './BottomTabNavigation';
import type { NavigationItem } from '@/types/navigation';

// User decision 2026-08-25 — EXACTLY 5 tabs GIỐNG HỆT mobile TAB_CONFIG:
// Trang chủ / Lịch / Chấm công (center, floating) / Tiện ích / Tôi.
export const EMPLOYEE_BOTTOM_NAV_ITEMS: NavigationItem[] = [
  { id: 'home', icon: Home, label: 'Trang chủ', path: '/dashboard/staff' },
  { id: 'schedule', icon: Calendar, label: 'Lịch', path: '/schedule' },
  { id: 'attendance', icon: Fingerprint, label: 'Chấm công', path: '/attendance', isCenter: true },
  { id: 'utilities', icon: LayoutGrid, label: 'Tiện ích', path: '/utilities' },
  { id: 'profile', icon: User, label: 'Tôi', path: '/profile' },
];

export const EmployeeBottomNav: React.FC = () => {
  const location = useLocation();
  return <BottomTabNavigation items={EMPLOYEE_BOTTOM_NAV_ITEMS} currentPath={location.pathname} />;
};

export default EmployeeBottomNav;
